/**
 * CertificateView — Vista imprimible del certificado médico.
 *
 * Layout inspirado en el certificado real solicitado por el usuario:
 * membrete con logo/clínica, encabezado con nombre del doctor y datos de
 * contacto, cuerpo con "Yo Dr. X. Provisto del Exequátur… Certifico…",
 * y pie con firma + sello (imágenes subidas al perfil).
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../../lib/api";
import { ArrowLeft, Printer, ArrowRight } from "@/components/icons";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
}

export default function CertificateView() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/certificates/${id}`)
      .then((r) => setCert(r.data))
      .catch(() => toast.error("No se pudo cargar el certificado"))
      .finally(() => setLoading(false));
  }, [id]);

  const returnTo = location.state?.returnTo;
  const returnState = location.state?.returnState;

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-black/60">
        Cargando certificado…
      </div>
    );
  if (!cert) return null;

  const doctorTitle = /^dr|^dra/i.test(cert.doctor_name || "")
    ? cert.doctor_name
    : `Dr. ${cert.doctor_name}`;
  const specialty = cert.doctor_specialty || "";
  const cedula = cert.cedula || "";
  const exequatur = cert.exequatur_number || "";

  // El texto principal del certificado, plantilla estándar en español.
  const bodyText = [
    `Yo ${doctorTitle}${exequatur ? `. Provisto del Exequátur No ${exequatur}` : ""}${cedula ? `. Ced: ${cedula}` : ""}. Certifico haber examinado a: ${cert.patient_name.toUpperCase()} y constatado que fue evaluado(a) en este centro de salud con el DX: ${cert.diagnosis.toUpperCase()}.`,
    cert.note ? `NOTA: ${cert.note.toUpperCase()}` : "",
    `Expedida la presente certificación en: ${cert.place || "___________"} ${cert.reason || "a petición del interesado"}.`,
  ]
    .filter(Boolean)
    .join("\n\n");

  return (
    <div className="min-h-screen bg-[#F5F5F5] py-8 print:bg-white print:py-0">
      {/* Toolbar — oculta al imprimir */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between mb-4 print:hidden">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-bold text-black/70 hover:text-black"
        >
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            data-testid="cert-print"
            className="btn-primary"
          >
            <Printer className="w-4 h-4 inline mr-1.5" />
            Imprimir
          </button>
          {returnTo && (
            <button
              onClick={() => navigate(returnTo, { state: returnState, replace: true })}
              data-testid="cert-continue-next"
              className="inline-flex items-center gap-1.5 bg-white text-black text-sm font-bold rounded-full px-4 py-2 border border-black/10 hover:bg-black/[0.03] transition"
            >
              Continuar
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Documento — ancho A4 (210mm), altura auto */}
      <div
        className="print-page max-w-4xl mx-auto bg-white shadow-[0_10px_40px_rgba(0,0,0,0.08)] px-10 py-8 print:shadow-none print:max-w-none print:px-14 print:py-10"
        data-testid="certificate-document"
      >
        {/* Membrete */}
        <div className="text-center">
          {cert.clinic_logo_url ? (
            <div className="flex items-center justify-center gap-3 mb-2">
              <img
                src={cert.clinic_logo_url}
                alt="Logo clínica"
                className="h-14 object-contain"
              />
              {cert.clinic_name && (
                <div className="text-left">
                  <div className="text-sm font-extrabold uppercase tracking-tight leading-tight text-[#1E4FBF]">
                    {cert.clinic_name}
                  </div>
                </div>
              )}
            </div>
          ) : (
            cert.clinic_name && (
              <div className="text-lg font-extrabold uppercase tracking-tight mb-2 text-black">
                {cert.clinic_name}
              </div>
            )
          )}
          <div className="text-base font-extrabold text-black">
            {doctorTitle}
            {specialty ? ` — ${specialty}` : ""}
          </div>
          {cert.clinic_address && (
            <div className="text-sm text-black mt-1 leading-snug max-w-2xl mx-auto">
              {cert.clinic_address}
            </div>
          )}
          {cert.clinic_phone && (
            <div className="text-sm font-bold text-black mt-0.5">
              {cert.clinic_phone}
            </div>
          )}
        </div>

        <div className="border-t border-black/10 my-6" />

        {/* Header con nombre del paciente y fecha */}
        <div className="flex items-center justify-between text-sm mb-4">
          <div className="font-bold text-black">{cert.patient_name}</div>
          <div className="text-black">{fmtDate(cert.date_iso)}</div>
        </div>

        {/* Cuerpo del certificado */}
        <div className="bg-[#F5F5F5] rounded-md p-5 text-sm leading-relaxed text-black whitespace-pre-line">
          {bodyText}
        </div>

        {/* Firma, sello y pie de firma */}
        <div className="mt-24 relative">
          {/* Sello — imagen posicionada centrada bajo la firma */}
          <div className="flex flex-col items-center">
            <div className="relative">
              {cert.stamp_url && (
                <img
                  src={cert.stamp_url}
                  alt="Sello"
                  className="absolute -top-14 left-1/2 -translate-x-1/2 h-32 opacity-80 pointer-events-none"
                />
              )}
              {cert.signature_url && (
                <img
                  src={cert.signature_url}
                  alt="Firma"
                  className="relative h-16 object-contain z-10"
                />
              )}
            </div>
            <div className="w-80 border-t border-black mt-1 pt-2 text-center">
              <div className="text-sm font-bold text-black">{doctorTitle}</div>
              {specialty && (
                <div className="text-sm text-black">{specialty}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          /* Ancho A4 exacto (210mm), altura automática según contenido */
          @page { size: 210mm auto; margin: 12mm; }
          html, body { background: #fff !important; }
          body * { visibility: hidden; }
          .print-page, .print-page * { visibility: visible; }
          .print-page {
            position: absolute;
            left: 0; top: 0;
            width: 186mm !important;
            max-width: 186mm !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}
